import React, { useState } from 'react';
import RegistrationModal from '../registration/RegistrationModal';
import { CategoryListingSkeleton } from '../../common/SkeletonLoader';
import './Categories.scss';

interface Category {
  id: number;
  name: string;
  price: number;
  price_display: string;
  description: string;
  quantity: number;
  sold_count: number;
  remaining: number;
  sold_out: boolean;
  hidden: boolean;
  highlight: boolean;
  registration_type: string;
  tag: string;
  priority: number;
  currency?: {
    symbol: string;
  };
  show_any_amount?: boolean;
}

interface Cluster {
  id: number;
  name: string;
  description: string;
  color: string;
  icon: string;
  display_order: number;
  is_active: boolean;
  categories: Category[];
}

interface OrganizedCategories {
  unclustered: Category[];
  clustered: Record<string, Cluster>;
}

interface CategoriesProps {
  event: any;
  user?: any;
  loading?: boolean;
  error?: string | null;
}

const Categories: React.FC<CategoriesProps> = ({ event, user, loading = false, error = null }) => {
  const [collapsedClusters, setCollapsedClusters] = useState<Set<number>>(new Set());
  const [registrationModal, setRegistrationModal] = useState<{
    show: boolean;
    category: Category | null;
    type: 'general' | 'individual' | 'donation' | 'whitelist' | 'team';
  }>({
    show: false,
    category: null,
    type: 'general'
  });

  if (loading) {
    return (
      <div className="categories-container">
        <CategoryListingSkeleton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="categories-container">
        <div className="alert alert-warning" role="alert">
          <strong>Categories Loading Error:</strong> {error}
        </div>
      </div>
    );
  }

  if (!event) {
    return null;
  }

  // Check if categories should be displayed
  const shouldShowCategories = () => {
    // Show categories if event has categories (like Django template)
    const organizedCategories = event.organized_categories;
    if (!organizedCategories) {
      return false;
    }

    const hasUnclustered = organizedCategories.unclustered && organizedCategories.unclustered.length > 0;
    const hasClustered = organizedCategories.clustered && Object.keys(organizedCategories.clustered).length > 0;
    
    return hasUnclustered || hasClustered;
  };

  if (!shouldShowCategories()) {
    return null;
  }

  const organizedCategories: OrganizedCategories = event.organized_categories;

  const formatPrice = (category: Category) => {
    if (category.price === 0) {
      return 'Free';
    }
    
    if (category.show_any_amount) {
      return category.price_display || `$${category.price.toFixed(2)}`;
    }
    
    const symbol = category.currency?.symbol || '$';
    return `${symbol} ${category.price.toLocaleString()}`;
  };

  const toggleCluster = (clusterId: number) => {
    const newCollapsed = new Set(collapsedClusters);
    if (newCollapsed.has(clusterId)) {
      newCollapsed.delete(clusterId);
    } else {
      newCollapsed.add(clusterId);
    }
    setCollapsedClusters(newCollapsed);
  };

  const handleRegistrationClick = (category: Category) => {
    // Determine registration type based on category properties
    let registrationType: 'general' | 'individual' | 'donation' | 'whitelist' | 'team' = 'general';
    
    // Map registration types from backend to frontend components
    // Based on EventPassCategory.REGISTRATION_TYPES in event/models.py
    switch (category.registration_type?.toLowerCase()) {
      case 'individual':
        registrationType = 'individual';
        break;
      case 'donation':
        registrationType = 'donation';
        break;
      case 'whitelisted':
        registrationType = 'whitelist';
        break;
      case 'team':
        registrationType = 'team';
        break;
      case 'direct':
      case 'pay_for_friend':
      case 'general':
      default:
        registrationType = 'general';
        break;
    }
    
    // Additional logic based on category properties
    if (category.show_any_amount && category.price === 0) {
      registrationType = 'donation';
    }
    
    setRegistrationModal({
      show: true,
      category,
      type: registrationType
    });
  };

  const handleCloseModal = () => {
    setRegistrationModal({
      show: false,
      category: null,
      type: 'general'
    });
  };

  const renderCategoryCard = (category: Category) => {
    const isSoldOut = category.sold_out || category.remaining <= 0;
    const canRegister = event.setting.live && !event.setting.on_page_registration && !isSoldOut;
    
    return (
      <div 
        key={category.id} 
        className={`category-card ${category.highlight ? 'highlighted' : ''} ${isSoldOut ? 'sold-out' : ''}`}
      >
        <div className="category-header">
          <h4 className="category-name">{category.name}</h4>
          <div className="category-price">
            {isSoldOut && (
              <span className="sold-out-badge">
                <i className="fas fa-times-circle"></i>
                {event.content.sold_out_text || 'Sold Out'}
              </span>
            )}
            <span className="price-tag">{formatPrice(category)}</span>
          </div>
        </div>

        {category.description && (
          <div className="category-description">
            <p>{category.description}</p>
          </div>
        )}

        <div className="category-footer">
          {!isSoldOut && event.setting.show_remaining_passes && (
            <div className="category-status">
              <span className="remaining-passes">
                <i className="fas fa-ticket-alt me-1"></i>
                {category.remaining} Left
              </span>
            </div>
          )}
          
          {canRegister ? (
            <button 
              onClick={() => handleRegistrationClick(category)}
              className="category-action-btn"
            >
              <span>{event.content.button_label || 'Register'}</span>
              <i className="fas fa-arrow-right ms-2"></i>
            </button>
          ) : isSoldOut ? (
            <button 
              className="category-action-btn sold-out-btn"
              disabled
            >
              <span>{event.content.sold_out_text || 'Sold Out'}</span>
              <i className="fas fa-times-circle ms-2"></i>
            </button>
          ) : !event.setting.live ? (
            <button 
              className="category-action-btn disabled-btn"
              disabled
            >
              <span>Registration Not Available</span>
              <i className="fas fa-clock ms-2"></i>
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="categories-container">

      {/* First show unclustered categories */}
      {organizedCategories.unclustered && organizedCategories.unclustered.length > 0 && (
        <>
          {organizedCategories.unclustered.map((category: Category) => 
            !category.hidden ? renderCategoryCard(category) : null
          )}
        </>
      )}

      {/* Then show clustered categories grouped by cluster */}
      {organizedCategories.clustered && Object.keys(organizedCategories.clustered).length > 0 && (
        <>
          {Object.entries(organizedCategories.clustered).map(([clusterId, cluster]: [string, Cluster]) => (
            <div key={clusterId} className="category-group">
              <div 
                className="category-group-header" 
                onClick={() => toggleCluster(parseInt(clusterId))}
              >
                <h5 className="category-group-title">
                  {cluster.icon && <i className={`${cluster.icon} me-2`}></i>}
                  {cluster.name}
                </h5>
                {cluster.description && (
                  <p className="category-group-description">{cluster.description}</p>
                )}
                <i className={`fas fa-chevron-${collapsedClusters.has(parseInt(clusterId)) ? 'down' : 'up'} category-group-icon`}></i>
              </div>
              <div 
                className="category-group-content"
                style={{ display: collapsedClusters.has(parseInt(clusterId)) ? 'none' : 'block' }}
              >
                {cluster.categories.map((category: Category) => 
                  !category.hidden ? renderCategoryCard(category) : null
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Registration Modal */}
      {registrationModal.category && (
        <RegistrationModal
          show={registrationModal.show}
          onHide={handleCloseModal}
          event={event}
          category={registrationModal.category}
          user={user}
          registrationType={registrationModal.type}
        />
      )}
    </div>
  );
};

export default Categories;
